/**
 * Media URL helper functions
 * Supports CDN providers and image presets
 */

import { getImageUrl } from './utils';

export interface MediaVariant {
  key: string;
  url: string;
  width: number;
  height: number;
  format: string;
  sizeBytes: number;
}

export interface MediaAsset {
  id?: number;
  url?: string;
  urlOriginal?: string;
  variants?: Record<string, MediaVariant>;
  provider?: string;
  original_key?: string;
}

/**
 * Get media URL with preset selection
 * @param media - Media asset object (from DB or API)
 * @param presetKey - Preset key (e.g., 'headline', 'card', 'detail', 'thumbnail')
 * @param retina - Whether to use retina (@2x) variant
 * @returns Public URL for the media
 */
export function getMediaUrl(
  media: MediaAsset | string | null | undefined,
  presetKey?: string,
  retina: boolean = false,
): string {
  // Handle null/undefined (S3 only - no placeholders)
  if (!media) {
    return '';
  }

  // Handle string (legacy path)
  if (typeof media === 'string') {
    // If it's already a full URL, return as is
    if (media.startsWith('http://') || media.startsWith('https://') || media.startsWith('data:') || media.startsWith('blob:')) {
      return media;
    }
    // Otherwise, treat as legacy path and use original getImageUrl
    return getImageUrl(media, presetKey);
  }

  // Handle MediaAsset object
  const asset = media as MediaAsset;

  // If no preset requested, return original
  if (!presetKey) {
    return asset.urlOriginal || asset.url || '';
  }

  // Try to find variant
  if (asset.variants) {
    const variantKey = retina ? `${presetKey}@2x` : presetKey;
    const variant = asset.variants[variantKey] || asset.variants[presetKey];

    if (variant && variant.url) {
      return variant.url;
    }
  }

  // Fallback to original
  return asset.urlOriginal || asset.url || '';
}

/**
 * Pick the best preset for a given use case
 * @param presetGroup - Preset group name (e.g., 'headline', 'card', 'detail')
 * @param media - Media asset
 * @param retina - Whether to use retina variant
 * @returns URL for the selected preset
 */
export function pickPreset(
  presetGroup: 'headline' | 'card' | 'detail' | 'thumbnail',
  media: MediaAsset | string | null | undefined,
  retina: boolean = false,
): string {
  return getMediaUrl(media, presetGroup, retina);
}

/**
 * Get image dimensions from variant
 */
export function getMediaDimensions(
  media: MediaAsset,
  presetKey?: string,
  retina: boolean = false,
): { width: number; height: number } | null {
  if (!media || !media.variants || !presetKey) {
    return null;
  }

  const variantKey = retina ? `${presetKey}@2x` : presetKey;
  const variant = media.variants[variantKey] || media.variants[presetKey];

  if (variant) {
    return { width: variant.width, height: variant.height };
  }

  // Fallback to original dimensions if available
  if (media.variants.original) {
    return { width: media.variants.original.width, height: media.variants.original.height };
  }

  return null;
}
